'use client'

import React, { useState, useEffect } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Search } from 'lucide-react'
import CampsFilter from './CampsFilter'
import AnmeldungenViewWrapper from './AnmeldungenViewWrapper.client'

interface CampsOverviewProps {
  initialCamps?: any[]
  summary?: any
}

const CampsOverview: React.FC<CampsOverviewProps> = ({ initialCamps, summary }) => {
  const [loading, setLoading] = useState(true)
  const [camps, setCamps] = useState(initialCamps || [])
  const [onlyWithRegistrations, setOnlyWithRegistrations] = useState(true)
  const [onlyFutureCamps, setOnlyFutureCamps] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [emailSearch, setEmailSearch] = useState('') // New state for email search

  // Use initialCamps if provided, otherwise fetch
  useEffect(() => {
    if (initialCamps) {
      setCamps(initialCamps)
      setLoading(false)
    } else {
      // Fetch logic here if needed
    }
  }, [initialCamps])

  // Fetch camps when filters change
  useEffect(() => {
    const fetchFilteredCamps = async () => {
      setLoading(true)
      try {
        const params = new URLSearchParams({
          onlyWithRegistrations: onlyWithRegistrations.toString(),
          onlyFutureCamps: onlyFutureCamps.toString(),
        })

        // Add email search parameter if provided
        if (emailSearch.trim()) {
          params.append('emailSearch', emailSearch.trim())
        }

        const response = await fetch(`/api/admin/camps-overview?${params}`)
        const data = await response.json()
        setCamps(data.camps)
      } catch (error) {
        console.error('Error fetching filtered camps:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchFilteredCamps()
  }, [onlyWithRegistrations, onlyFutureCamps, emailSearch]) // Add emailSearch to dependencies

  // Create grouped data
  const campsGroupedData = camps.reduce((acc: any, camp: any) => {
    acc[camp.id] = {
      camp: camp,
      anmeldungen: [],
      totalRegistrations: camp.totalRegistrations,
      openRegistrations: camp.openRegistrations,
      statusBreakdown: camp.statusBreakdown,
    }
    return acc
  }, {})

  return (
    <Card className="shadow-2xs border-none bg-white">
      <CardContent className="space-y-6">
        {/* Combined search bar and filters */}
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex flex-1 items-center gap-4">
              <div className="max-w-md flex-1">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Camp suchen..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full rounded-md border border-gray-300 py-2 pl-10 pr-4 focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                  />
                  <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                </div>
              </div>

              {/* NEW: Email search input */}
              <div className="max-w-md flex-1">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="E-Mail, Vorname oder Nachname suchen..."
                    value={emailSearch}
                    onChange={(e) => setEmailSearch(e.target.value)}
                    className="w-full rounded-md border border-gray-300 py-2 pl-10 pr-4 focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                  />
                  <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                </div>
              </div>
            </div>
            <CampsFilter
              onlyWithRegistrations={onlyWithRegistrations}
              onlyFutureCamps={onlyFutureCamps}
              onOnlyWithRegistrationsChange={setOnlyWithRegistrations}
              onOnlyFutureCampsChange={setOnlyFutureCamps}
            />
          </div>
        </div>

        {loading ? (
          <div className="py-4 text-center">Loading...</div>
        ) : (
          <AnmeldungenViewWrapper
            anmeldungen={[]}
            groupedAnmeldungen={campsGroupedData}
            selectedCampId={undefined}
            searchTerm={searchTerm}
            emailSearch={emailSearch}
          />
        )}
      </CardContent>
    </Card>
  )
}

export default CampsOverview
